import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import './Page8.css';

const PERSONAS = {
  OnPoint: {
    label: "🎯 OnPoint",
    prompt: "Deliver the absolute minimum words for accuracy. No fluff. Raw, high-density data."
  },
  Poetic: {
    label: "🍷 Poetic",
    prompt: "Exert brevity. Deliver surgical precision wrapped in a dry, Oscar Wilde-esque wit. Speak as a peer who knows too much but says just enough."
  },
  Engineer: {
    label: "⚙️ Engineer",
    prompt: "Focus on mechanics and implementation. Use Markdown structure. Prioritize technical feasibility."
  },
  Oracle: {
    label: "🔮 Oracle",
    prompt: "Speak in brief, profound truths. Be slightly cryptic but forward-thinking."
  },
  Rogue: {
    label: "🕶️ Rogue",
    prompt: "Fast and informal. Use punchy language. Be brutally honest. No 'AI-assistant' clichés."
  },
  Kaiser: {
    label: "👑 Kaiser",
    prompt: "Speak as Kaiser Wilhelm II. Your tone is imperial, autocratic, and deeply formal. Refer to the glory of the Empire and Prussian discipline. Be grandiloquent and authoritative. Use the royal 'We'."
  }
};

function Page8() {
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState(() => {
    try {
      const saved = localStorage.getItem('page8_chat_history');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Restore Date objects for timestamps
        return parsed.map(msg => ({ ...msg, timestamp: new Date(msg.timestamp) }));
      }
    } catch (e) {}
    return [];
  });
  const [generatingSeconds, setGeneratingSeconds] = useState(0);
  const [selectedPersona, setSelectedPersona] = useState('Poetic');
  const chatContainerRef = useRef(null);

  // Persist chat history to localStorage
  useEffect(() => {
    try {
      // Keep last 50 messages to avoid localStorage bloat
      const toSave = chatHistory.slice(-50);
      localStorage.setItem('page8_chat_history', JSON.stringify(toSave));
    } catch (e) {}
  }, [chatHistory]);

  const sendMessage = async () => {
    if (!inputText.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputText.trim(),
      timestamp: new Date()
    };

    setChatHistory(prev => [...prev, userMessage]);
    const currentInput = inputText.trim();
    setInputText('');
    setIsLoading(true);

    try {
      // Build history from last 4 exchanges (user + ai pairs)
      const recentHistory = chatHistory
        .filter(msg => msg.type === 'user' || msg.type === 'ai')
        .slice(-8)
        .map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.content,
        }));

      const response = await axios.post(
        'https://kidr78home.duckdns.org/api/chat',
        {
          sysmsg: PERSONAS[selectedPersona].prompt,
          chatmsg: currentInput,
          history: recentHistory,
        },
        {
          headers: { Authorization: 'kSuIlUmNaTiIeO5' },
        }
      );

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.data.resp || 'No response received.',
        timestamp: new Date(),
        persona: selectedPersona 
      };

      setChatHistory(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      setChatHistory(prev => [...prev, { id: Date.now() + 1, type: 'error', content: 'Failed to send.', timestamp: new Date() }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Auto-scroll
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory, isLoading]);

  // Timer
  useEffect(() => {
    let intervalId;
    if (isLoading) {
      intervalId = setInterval(() => setGeneratingSeconds(p => p + 1), 1000);
    } else {
      clearInterval(intervalId);
      setGeneratingSeconds(0);
    }
    return () => clearInterval(intervalId);
  }, [isLoading]);

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-title-section">
          <h1 className="chat-title">💬 AI Assistant</h1>
          <div className="persona-selector">
            {Object.keys(PERSONAS).map((key) => (
              <button
                key={key}
                className={`persona-button ${selectedPersona === key ? 'active' : ''}`}
                onClick={() => setSelectedPersona(key)}
              >
                {PERSONAS[key].label}
              </button>
            ))}
          </div>
        </div>
        {chatHistory.length > 0 && (
          <button className="clear-chat-button" onClick={() => setChatHistory([])}>🗑️ Clear</button>
        )}
      </div>

      <div className="chat-main">
        <div className="chat-messages" ref={chatContainerRef}>
          {chatHistory.length === 0 ? (
            <div className="welcome-section">
              <div className="welcome-message">
                <h2>👋 Welcome to AI Chat!</h2>
                <p>Pick a persona above and start typing.</p>
              </div>
            </div>
          ) : (
            chatHistory.map((msg) => (
              <div key={msg.id} className={`message ${msg.type}`}>
                <div className="message-avatar">
                  {msg.type === 'user' ? '👤' : msg.type === 'error' ? '❌' : '🤖'}
                </div>
                <div className="message-content">
                  <div className="message-header">
                    <span className="message-sender">
                      {msg.type === 'user' ? 'You' : `AI (${msg.persona || 'Default'})`}
                    </span>
                    <span className="message-time">
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="message-text">
                    {msg.type === 'ai' ? <ReactMarkdown>{msg.content}</ReactMarkdown> : <p>{msg.content}</p>}
                  </div>
                </div>
              </div>
            ))
          )}

          {isLoading && (
            <div className="message ai loading">
              <div className="message-avatar">🤖</div>
              <div className="message-content">
                <div className="typing-indicator">
                  <div className="typing-dots"><span></span><span></span><span></span></div>
                  <span className="typing-text">Thinking... ({generatingSeconds}s)</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="chat-input-section">
          <div className="chat-input-container">
            <textarea
              className="chat-input"
              rows="1"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={isLoading}
            />
            <button className={`send-button ${isLoading ? 'loading' : ''}`} onClick={sendMessage} disabled={isLoading || !inputText.trim()}>
              {isLoading ? <span className="loading-spinner"></span> : <span className="send-icon">📤</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Page8;

